/**
 * useAsyncInstitutionData Hook
 *
 * React hook for fetching institution data from the BigQuery API.
 * Provides async data fetching with loading states and error handling.
 * Used by Analyst Bench components to get rating details for any institution.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getInstitutionPeriodDataAsync,
  getInstitutionDataAsync,
  getInstitutionPeriodsAsync,
  type PeriodData,
  type InstitutionData,
} from '../context/AnalystBenchContext';

// ============================================================================
// PERIOD DATA HOOK
// ============================================================================

interface UsePeriodDataState {
  data: PeriodData | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch period-specific data for an institution (async with API support)
 */
export function usePeriodData(institutionId: string | null | undefined, period: string | null | undefined) {
  const [state, setState] = useState<UsePeriodDataState>({
    data: null,
    isLoading: false,
    error: null,
  });

  // Track current request to avoid race conditions
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!institutionId || !period) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }

    const requestId = ++requestIdRef.current;
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await getInstitutionPeriodDataAsync(institutionId, period);

      // Only update if this is still the current request
      if (requestId === requestIdRef.current) {
        setState({ data, isLoading: false, error: null });
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        console.error('[usePeriodData] Error:', err);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Failed to fetch period data',
        }));
      }
    }
  }, [institutionId, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// ============================================================================
// INSTITUTION DATA HOOK
// ============================================================================

interface UseInstitutionDataState {
  data: InstitutionData | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch full institution data including history (async with API support)
 */
export function useInstitutionDataAsync(institutionId: string | null | undefined) {
  const [state, setState] = useState<UseInstitutionDataState>({
    data: null,
    isLoading: false,
    error: null,
  });

  // Track current request to avoid race conditions
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!institutionId) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }

    const requestId = ++requestIdRef.current;
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const data = await getInstitutionDataAsync(institutionId);

      // Only update if this is still the current request
      if (requestId === requestIdRef.current) {
        setState({ data, isLoading: false, error: null });
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        console.error('[useInstitutionDataAsync] Error:', err);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Failed to fetch institution data',
        }));
      }
    }
  }, [institutionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    refetch: fetchData,
  };
}

// ============================================================================
// PERIODS HOOK
// ============================================================================

interface UseInstitutionPeriodsState {
  periods: string[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch available periods for an institution (async with API support)
 */
export function useInstitutionPeriods(institutionId: string | null | undefined) {
  const [state, setState] = useState<UseInstitutionPeriodsState>({
    periods: [],
    isLoading: false,
    error: null,
  });

  // Track current request to avoid race conditions
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!institutionId) {
      setState({ periods: [], isLoading: false, error: null });
      return;
    }

    const requestId = ++requestIdRef.current;
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const periods = await getInstitutionPeriodsAsync(institutionId);

      // Only update if this is still the current request
      if (requestId === requestIdRef.current) {
        setState({ periods, isLoading: false, error: null });
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        console.error('[useInstitutionPeriods] Error:', err);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Failed to fetch periods',
        }));
      }
    }
  }, [institutionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    refetch: fetchData,
  };
}

export default {
  usePeriodData,
  useInstitutionDataAsync,
  useInstitutionPeriods,
};
